import { GlobalStyles, Stack, styled } from '@mui/material';
import { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { ConversationChannelTypeExtended } from '@provision/chat-utils';
import {
  ChannelIdentifiers,
  ConversationChannelType,
  ConversationDraftSideEffectActionType,
  ConversationParticipationMode,
  ConversationStatus,
} from '@provision/conversation-types';
import {
  getAttachmentsFromEditor,
  isFile,
  RichTextEditorFile,
} from '@provision/rich-text-editor';
import { motion } from 'framer-motion';
import React, {
  memo,
  RefObject,
  UIEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import 'react-chat-elements/dist/main.css';
import { useResizable } from 'react-resizable-layout';
import { isNullish } from 'remeda';
import { GLOBAL_TABS_HEIGHT } from 'src/components/GlobalTabs/styled';
import { ResizeHandle } from 'src/components/ResizeHandle';
import {
  Conversation,
  ConversationDataForSubmission,
  ConversationParticipantItem,
  MacroActionRule,
  SubmitConversationDataParticipant,
  useCurrentUserProfile,
  useGroupRules,
  usePartners,
  UserGroupRules,
} from 'src/graphql';
import { getConversationChannelTypeByParticipation } from 'src/graphql/entities/conversation/conversation.mappings';
import { useConversationParticipationRecords } from 'src/graphql/entities/conversation-participation/hooks/use-conversation-participation-records';
import { useFileUpload } from 'src/graphql/entities/files-upload';
import { UserPresence } from 'src/graphql/entities/user-presence/user-presence';
import { InputField } from 'src/pages/Conversations';
import { ComposerBannersProvider } from 'src/pages/Conversations/Chat/Banners/composer-banners-provider';
import { CONVERSATION_COLUMN_MAX_WIDTH } from 'src/pages/Conversations/constants';
import { useChatInputStore } from 'src/store/chat-input';
import { useNotifications } from 'src/utils/hooks';
import { useScreenDimensions } from 'src/utils/system';
import useResizeObserver from 'use-resize-observer';
import { HolidayBanner } from './Banners/holiday-banner';
import { SnoozeBanner } from './Banners/snooze-banner';
import { ComposerStrip } from './ComposerStrip/composer-strip';
import { useStripRequesterFirstName } from './ComposerStrip/use-strip-requester-first-name';
import { DraftScheduleSendAndAction } from './conversation-draft-schedule';
import { ConversationFooterStatePanel } from './ConversationStatePanel/conversation-footer-state-panel';
import { useConversationFooterState } from './ConversationStatePanel/use-conversation-footer-state';
import { InputFieldHandle } from './InputField/input-field';
import { JumpToNewMessagesPill } from './jump-to-new-messages-pill';
import { NewMessagesAnchorProvider } from './new-messages-anchor-provider';
import { useNewMessagesBoundary } from './new-messages-boundary-context';
import { NewMessagesBoundaryProvider } from './new-messages-boundary-provider';
import { useSelectedParticipants } from './ParticipationPanel/use-selected-participants';
import ChatThread from './registry/ChatThread';
import { useChatScrollHandler } from './registry/hooks';
import { ChatRegistryProvider } from './registry/provider';
import { SmsSessionPanel } from './SmsSession/sms-session-panel';
import { SmsSessionProvider } from './SmsSession/sms-session-provider';
import { useChatAttachmentsSubscription } from './use-chat-attachements-subscription';
import { useComposerDockHeight } from './use-composer-dock-height';
import { useComposerFold } from './use-composer-fold';
import { useConversationTypeOptions } from './use-conversation-type-options';

const MessagesList = styled(Stack, {
  name: 'MessagesList ',
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  flex: 1,
  // Header and composer dock float over the thread instead of taking layout space, so
  // reserve their heights here; the dock's is measured (resizable, and banners come and go).
  paddingTop: `calc(${GLOBAL_TABS_HEIGHT} + ${theme.spacing(1)})`,
  overflow: 'auto',
  flexDirection: 'column-reverse',
  paddingBottom: 'calc(var(--composer-dock-h, 0px) + 8px)',
  // column-reverse + a changing bottom padding is where scroll anchoring misfires
  overflowAnchor: 'none',
  // scroller must run to the container's bottom edge, under the floating dock
  marginBottom: 0,
  ...(isMobile
    ? {
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }
    : {}),
}));

const ThreadArea = styled(Stack, {
  name: 'ThreadArea',
})(() => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  flex: 1,
  minHeight: 0,
}));

function buildSendAndOverrides(actions?: DraftScheduleSendAndAction[]): {
  statusId?: ConversationStatus;
  snoozeUntil?: string;
} {
  if (!actions?.length) {
    return {};
  }

  let statusId: ConversationStatus | undefined;
  let snoozeUntil: string | undefined;

  actions.forEach((action) => {
    if (action.type === ConversationDraftSideEffectActionType.Status) {
      statusId = action.value as ConversationStatus;
    } else if (action.type === ConversationDraftSideEffectActionType.Snooze && action.value) {
      snoozeUntil = action.value;

      if (!statusId) {
        statusId = ConversationStatus.Snoozed;
      }
    }
  });

  if (statusId && statusId !== ConversationStatus.Snoozed) {
    snoozeUntil = undefined;
  }

  return { statusId, snoozeUntil };
}

const Wrapper = styled(Stack, {
  name: 'Wrapper ',
})(({ theme }) => ({
  height: '100%',
  // Only MessagesList scrolls: a scrollable Wrapper would gain a second, competing scrollbar
  // whenever the floating dock (resize + banners) grows past its box.
  overflow: 'hidden',
  gap: 1,
  // containing block for the composer dock, which floats over the thread
  position: 'relative',
  '& .editor-content': {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
    '& .portal-editor': {
      maxHeight: 'none',
      [theme.breakpoints.down('sm')]: {
        fontSize: '1rem',
      },
    },
  },
}));

const CHAT_INPUT_MIN_HEIGHT_PX = 250;

// Mobile has no resize handle, so autogrow always races straight to the cap — 80% left the
// thread almost nothing once banners stacked with a long draft
const CHAT_INPUT_MOBILE_MAX_HEIGHT_PERCENT = 58;
const CHAT_INPUT_DESKTOP_MAX_HEIGHT_PERCENT = 90;

// Floor for the percent above (via CSS max()) on short viewports: the worst realistic mobile
// stack — all three banners atop the card's 12rem Public Reply floor — measures ~321px.
const CHAT_INPUT_MOBILE_MIN_DOCK_HEIGHT_PX = 336;

// An expanded live SMS session sits inside the dock, above the banners and card. On the
// budget above it leaves the card less than its floor and overflow:hidden takes the send row
// off screen, so the dock borrows from the thread for as long as the panel stays open.
const CHAT_INPUT_MOBILE_SMS_EXPANDED_MAX_HEIGHT_PERCENT = 88;

// Dock floats this far above the window bottom, thread visibly scrolling beneath the gap.
// Px, not theme.spacing: shared with the drag-height arithmetic, which works in DOM pixels.
const COMPOSER_DOCK_BOTTOM_GAP_PX = 4;

// Card and strip are always mounted: folding animates them, the card keeps the editor's draft.
const MotionStack = motion.create(Stack);

interface ComposerDockSxParams {
  isDragging: boolean;
  isMobile: boolean;
  chatInputAutoHeight: boolean;
  isClosedConversation: boolean;
  isConversationDeleted: boolean | undefined;
  /** Folded, or mid fold/unfold animation: the dock follows its content instead of the drag height. */
  isComposerFolding: boolean;
  inputHeight: number;
}

const getComposerDockSx = (
  theme: Theme,
  {
    isDragging,
    isMobile,
    chatInputAutoHeight,
    isClosedConversation,
    isConversationDeleted,
    isComposerFolding,
    inputHeight,
  }: ComposerDockSxParams,
): SystemStyleObject<Theme> => ({
  ...(isDragging && { transition: 'none' }),
  // Caps the dock's TOTAL height (banners + card), not just the card.
  maxHeight: isMobile
    ? `max(${CHAT_INPUT_MOBILE_MAX_HEIGHT_PERCENT}%, ${CHAT_INPUT_MOBILE_MIN_DOCK_HEIGHT_PX}px)`
    : `${CHAT_INPUT_DESKTOP_MAX_HEIGHT_PERCENT}%`,
  ...(isMobile && {
    '&:has([data-sms-session-expanded])': {
      maxHeight: `${CHAT_INPUT_MOBILE_SMS_EXPANDED_MAX_HEIGHT_PERCENT}%`,
    },
  }),
  width: '100%',
  maxWidth: CONVERSATION_COLUMN_MAX_WIDTH,
  marginInline: 'auto',
  minHeight: isMobile ? 0 : 'fit-content',
  ...(!chatInputAutoHeight &&
    !isMobile &&
    !isClosedConversation &&
    !isConversationDeleted &&
    !isComposerFolding && {
      /*
        The handle grabs the top of THIS box and useResizable reports the distance from the
        container's bottom to the pointer, so the dragged value is this box's height plus the
        gap it floats above that bottom — driving the card alone offsets the top edge from
        the pointer by the banners' height. minHeight, not height, so banners can still push
        the dock taller; floored before the gap comes off, or the minimum re-adds it.
      */
      minHeight: Math.max(inputHeight, CHAT_INPUT_MIN_HEIGHT_PX) - COMPOSER_DOCK_BOTTOM_GAP_PX,
    }),
  // the dock floats over the thread instead of pushing it; the thread reserves
  // its height via --composer-dock-h
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: `${COMPOSER_DOCK_BOTTOM_GAP_PX}px`,
  zIndex: theme.zIndex.app.ServiceDeskChatComposer,
  // Dock never scrolls: banners keep their natural height, the card below flex-shrinks, and
  // overflow spills into the editor's own internal scroll.
  overflow: 'hidden',
  '& .editor-content': {
    minHeight: '3rem !important',
    ...(chatInputAutoHeight &&
      (isMobile
        ? {
            /*
              Mobile autogrow is bounded by the dock's own maxHeight, not a viewport
              fraction: a fixed cap can't know how much chrome sits above it, so with
              banners stacked the card outgrew the dock and overflow:hidden clipped the
              tools row away. `1 1 0` + minHeight:0 is what lets it shrink at all.
            */
            flex: '1 1 0',
            minHeight: 0,
            overflow: 'hidden',
          }
        : { maxHeight: '50vh' })),
  },
  ...(isMobile &&
    chatInputAutoHeight && {
      // Wrapper drops the editor's own 25rem cap; on mobile it scrolls inside whatever
      // height the flex row above granted it instead.
      '& .portal-editor': { maxHeight: '100%', overflow: 'auto' },
    }),
});

interface ChatProps {
  onSubmitConversationData: (input: ConversationDataForSubmission) => Promise<unknown>;
  conversation: Conversation;
  participants: ConversationParticipantItem[];
  userPresenceMap: Map<string, UserPresence>;
  onPermanentlyDeleted: () => void;
}

interface ChatThreadShellProps {
  isMobile: boolean;
  chatRef: RefObject<HTMLDivElement | null>;
  initialPositionDummyRef: RefObject<HTMLDivElement | null>;
  dockHeight: number;
  /** Set for one frame while the scroller is compensated for a dock-height change, so that
   * programmatic scrollTop write isn't misread as pagination or new-messages user intent. */
  suppressScrollHandlingRef: RefObject<boolean>;
}

const ChatThreadShell = ({
  isMobile,
  chatRef,
  initialPositionDummyRef,
  dockHeight,
  suppressScrollHandlingRef,
}: ChatThreadShellProps): React.JSX.Element => {
  const onPaginateScroll = useChatScrollHandler();
  const { count, jumpPillDirection, markUserIntent, onThreadScroll, jumpToDivider } =
    useNewMessagesBoundary();

  const onScroll = useCallback(
    (event: UIEvent) => {
      if (suppressScrollHandlingRef.current) return;
      void onPaginateScroll(event);
      onThreadScroll();
    },
    [onPaginateScroll, onThreadScroll, suppressScrollHandlingRef],
  );

  return (
    <ThreadArea
      style={
        {
          // exposed for the new-messages boundary inset and jump pill, which anchor above the dock
          '--composer-dock-h': `${dockHeight}px`,
        } as React.CSSProperties
      }
    >
      <MessagesList
        isMobile={isMobile}
        onScroll={onScroll}
        onWheel={markUserIntent}
        onTouchMove={markUserIntent}
        ref={chatRef}
        data-testid="chat-messages-list"
      >
        <div ref={initialPositionDummyRef} />
        <ChatThread />
      </MessagesList>
      {jumpPillDirection ? (
        <JumpToNewMessagesPill
          count={count}
          direction={jumpPillDirection}
          onClick={jumpToDivider}
          bottomInset={dockHeight}
        />
      ) : null}
    </ThreadArea>
  );
};

const ChatComponent = ({
  onSubmitConversationData,
  conversation,
  participants,
  userPresenceMap,
  onPermanentlyDeleted,
}: ChatProps): React.JSX.Element => {
  const { isMobile } = useScreenDimensions();
  const { partners } = usePartners();
  const { showSuccessAlert } = useNotifications();
  const initialPositionDummyRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const inputContainerRef = useRef<HTMLDivElement | null>(null);

  const chatInputHeight = useChatInputStore.use.chatInputHeight();
  const saveChatInputHeight = useChatInputStore.use.setChatInputHeight();
  const inputFieldRef = useRef<InputFieldHandle>(null);

  useChatAttachmentsSubscription(conversation.conversationId);

  const [chatInputAutoHeight, setChatInputAutoHeight] = useState<boolean>(
    isMobile || !chatInputHeight,
  );

  useEffect(() => {
    // resetting scroll value on switching between conversations
    if (initialPositionDummyRef.current) {
      initialPositionDummyRef.current.scrollIntoView();
    }
  }, [conversation?.conversationId]);

  const { uploadFile } = useFileUpload();
  const [files, setFiles] = useState<RichTextEditorFile[]>([]);

  const submitConversationData = useCallback(
    async ({
      message,
      macros,
      activeChannels,
      participants,
      sendAndActions,
    }: {
      message: { body: string; isPrivate: boolean; smsSessionId?: string };
      macros: (MacroActionRule & { isValid: boolean })[];
      activeChannels?: ChannelIdentifiers[];
      participants: SubmitConversationDataParticipant[];
      sendAndActions?: DraftScheduleSendAndAction[];
    }) => {
      if (!conversation) return;
      if (!message && !files.length) return;

      // type guard, as the file can be of type File or ConversationAttachment
      if (files.some((file) => !isFile(file))) return;

      const { statusId: statusOverride, snoozeUntil } = buildSendAndOverrides(sendAndActions);

      const attachments = await getAttachmentsFromEditor({
        files: files as File[],
        attachmentsBucketFolder: `${conversation?.partnerId}/${conversation?.conversationId}`,
        uploadFile,
      });

      const payload: ConversationDataForSubmission = {
        conversation: {
          partnerId: conversation.partnerId,
          subject: conversation.subject,
          type: conversation.type,
          urgency: conversation.urgency,
          workspaceConversationId: conversation.workspaceConversationId,
          requester: conversation.requester,
          ...(statusOverride ? { conversationStatusId: statusOverride } : {}),
          ...(snoozeUntil !== undefined ? { snoozeUntil } : {}),
        },
        message: { ...message, attachments },
        activeChannels,
        ignoreActiveChannelsFor: [
          {
            participationMode: ConversationParticipationMode.Assist,
            channelType: ConversationChannelType.Chat,
          },
        ],
        participants,
      };

      const hasMacrosApplied = macros.length > 0;

      if (hasMacrosApplied) {
        payload.macroActions = JSON.stringify(
          macros.reduce<MacroActionRule[]>(
            (acc, { isValid, id, ...action }) => (isValid ? [...acc, action] : acc),
            [],
          ),
        );
      }

      await onSubmitConversationData(payload);
      if (hasMacrosApplied) {
        showSuccessAlert('Actions with macro are applied ');
      }

      setFiles([]);
    },
    [files, conversation, uploadFile, onSubmitConversationData, showSuccessAlert],
  );

  const footerState = useConversationFooterState(conversation);
  const isChatReadOnly = footerState !== null;
  const composerFold = useComposerFold({ isChatReadOnly, inputFieldRef });
  const isComposerFolding = composerFold.isFolding;
  const stripRequesterFirstName = useStripRequesterFirstName(conversation);
  const canViewOriginalEmailOnly =
    isChatReadOnly &&
    (conversation?.conversationStatusId as ConversationStatus) === ConversationStatus.Closed;

  const conversationTypeOptions = useConversationTypeOptions('chat');

  const { ref: chatInputWrapperRef, height: chatInputWrapperObservableHeight } =
    useResizeObserver();
  // Same container useResizable drags against, so the drag clamps to what it can show.
  const { height: inputContainerObservableHeight } = useResizeObserver<HTMLDivElement>({
    ref: inputContainerRef as RefObject<HTMLDivElement>,
  });

  // Mirrors the dock's own CSS cap (getComposerDockSx's maxHeight, minus the bottom gap) so
  // the stored value never disagrees with where the box is painted. Without it useResizable
  // clamps to [min, Infinity] and a drag can push the handle out of the container for good.
  const maxInputHeight = inputContainerObservableHeight
    ? Math.max(
        (inputContainerObservableHeight *
          (isMobile
            ? CHAT_INPUT_MOBILE_MAX_HEIGHT_PERCENT
            : CHAT_INPUT_DESKTOP_MAX_HEIGHT_PERCENT)) /
          100 -
          COMPOSER_DOCK_BOTTOM_GAP_PX,
        CHAT_INPUT_MIN_HEIGHT_PX,
      )
    : Infinity;

  const {
    position: inputHeight,
    separatorProps,
    isDragging,
    setPosition: setChatInputHeight,
  } = useResizable({
    axis: 'y',
    initial: chatInputHeight || CHAT_INPUT_MIN_HEIGHT_PX,
    min: CHAT_INPUT_MIN_HEIGHT_PX,
    max: maxInputHeight,
    containerRef: inputContainerRef as RefObject<HTMLElement>,
    reverse: true,
    onResizeStart: () => {
      // Switching auto-height off hands sizing to the stored value, which is stale — seed it
      // with where the dock actually is so the press itself doesn't move anything.
      if (chatInputWrapperObservableHeight) {
        setChatInputHeight(chatInputWrapperObservableHeight + COMPOSER_DOCK_BOTTOM_GAP_PX);
      }
      setChatInputAutoHeight(false);
      if (inputContainerRef.current) {
        inputContainerRef.current.style.userSelect = 'none'; // disable text selection
      }
    },
    onResizeEnd: ({ position }) => {
      if (inputContainerRef.current) {
        inputContainerRef.current.style.userSelect = ''; // enable text selection
      }
      saveChatInputHeight(position);
    },
  });

  // `max` above only clamps live interactions, so this covers the two cases that aren't a
  // drag: a session-restored height that already exceeds the container, and the container
  // resizing later. Persisted too, so the stored height self-heals instead of re-clamping.
  useLayoutEffect(() => {
    if (!Number.isFinite(maxInputHeight) || inputHeight <= maxInputHeight) return;
    setChatInputHeight(maxInputHeight);
    saveChatInputHeight(maxInputHeight);
  }, [inputHeight, maxInputHeight, setChatInputHeight, saveChatInputHeight]);

  useEffect(() => {
    // we need to sync the chat input height with the observable height
    // to prevent jumping when the chat input is starting to resize
    if (
      !isMobile &&
      chatInputAutoHeight &&
      !isComposerFolding &&
      chatInputWrapperObservableHeight
    ) {
      // +gap so `inputHeight` always means the dock's full footprint, as onResizeStart and
      // getComposerDockSx both assume; the bare measurement is one gap short.
      setChatInputHeight(chatInputWrapperObservableHeight + COMPOSER_DOCK_BOTTOM_GAP_PX);
    }
  }, [
    isMobile,
    chatInputAutoHeight,
    isComposerFolding,
    chatInputWrapperObservableHeight,
    setChatInputHeight,
  ]);

  // Reserved thread clearance is the dock's height plus the gap it floats above the window
  // bottom. The fallback before the observer's first report must never be 0: reserving
  // nothing laid the opening skeleton under the dock, and it jumped once the measurement
  // landed — unmasked because a fresh conversation is pinned to the newest message, the one
  // case useComposerDockHeight's scroll compensation skips.
  const liveDockHeight = chatInputWrapperObservableHeight
    ? chatInputWrapperObservableHeight + COMPOSER_DOCK_BOTTOM_GAP_PX
    : Math.max(inputHeight, isMobile ? CHAT_INPUT_MOBILE_MIN_DOCK_HEIGHT_PX : 0);

  const { committedDockHeight, suppressScrollHandlingRef } = useComposerDockHeight({
    liveDockHeight,
    isDragging,
    chatRef,
  });

  const { user } = useCurrentUserProfile();

  const { conversationParticipations } = useConversationParticipationRecords({
    conversationId: conversation.id,
    includeDeleted: true,
  });

  const selectedParticipantsState = useSelectedParticipants({
    participations: conversationParticipations,
    conversation,
    participants,
    userId: user?.userId,
  });

  const isClosedConversation =
    (conversation.conversationStatusId as ConversationStatus) === ConversationStatus.Closed;

  const { groupRules } = useGroupRules();

  const commonData = useMemo(
    () => ({
      conversationId: conversation.conversationId,
      partnerId: String(conversation.partnerId),
      hasAssociatedAddresses: (conversation.associatedAddresses?.length ?? 0) > 0,
      partners: partners ?? {},
      userPresenceMap,
      conversationParticipations,
      selectedParticipantsState,
      channel: getConversationChannelTypeByParticipation(conversationParticipations),
      isChatReadOnly,
      canViewOriginalEmailOnly,
      groupRules: groupRules as Record<UserGroupRules, boolean>,
      chatRef,
    }),
    [
      conversation.conversationId,
      conversation.partnerId,
      conversation.associatedAddresses,
      partners,
      userPresenceMap,
      conversationParticipations,
      selectedParticipantsState,
      isChatReadOnly,
      canViewOriginalEmailOnly,
      groupRules,
      chatRef,
    ],
  );

  return (
    <Wrapper ref={inputContainerRef}>
      {isMobile ? (
        <GlobalStyles
          styles={{
            'html, body': {
              overscrollBehavior: 'none',
            },
          }}
        />
      ) : null}
      <NewMessagesAnchorProvider>
        <ChatRegistryProvider commonData={commonData}>
          <SmsSessionProvider
            conversationId={conversation.conversationId}
            participants={participants}
            requesterFirstName={conversation.requesterData?.firstName ?? null}
          >
            <NewMessagesBoundaryProvider
              currentUserId={user?.id}
              bottomInset={committedDockHeight}
            >
              <ChatThreadShell
                isMobile={isMobile}
                chatRef={chatRef}
                initialPositionDummyRef={initialPositionDummyRef}
                dockHeight={committedDockHeight}
                suppressScrollHandlingRef={suppressScrollHandlingRef}
              />
            </NewMessagesBoundaryProvider>
            <Stack
              ref={chatInputWrapperRef}
              sx={(theme) =>
                getComposerDockSx(theme, {
                  isDragging,
                  isMobile,
                  chatInputAutoHeight,
                  isClosedConversation,
                  isConversationDeleted: conversation.isDeleted,
                  isComposerFolding,
                  inputHeight,
                })
              }
            >
              {isDragging ? (
                <GlobalStyles
                  styles={{
                    body: {
                      userSelect: 'none',
                      cursor: 'ns-resize',
                    },
                  }}
                />
              ) : null}
              {!isMobile &&
                !isClosedConversation &&
                !conversation.isDeleted &&
                !isComposerFolding && (
                  // first child of the dock stack, so the grab edge spans the top of banners + card
                  <ResizeHandle
                    axis="y"
                    isDragging={isDragging}
                    {...separatorProps}
                    sx={{ top: '-0.3125rem', zIndex: 2 }}
                    data-testid="composer-resize-handle"
                  />
                )}
              {!isChatReadOnly && <SmsSessionPanel />}
              <ComposerBannersProvider>
                <Stack p={1} flexGrow={1} flexShrink={1} minHeight={0}>
                  {/* banners belong to the open card: folded, the strip stands alone */}
                  {!composerFold.isFolded && <SnoozeBanner conversation={conversation} />}
                  {footerState ? (
                    <>
                      {!isNullish(conversation.partnerId) && (
                        <HolidayBanner partnerId={conversation.partnerId} />
                      )}
                      <ConversationFooterStatePanel
                        state={footerState}
                        conversation={conversation}
                        onPermanentlyDeleted={onPermanentlyDeleted}
                      />
                    </>
                  ) : (
                    <>
                      {/* The dock owns the resized height; the card fills whatever room the
                          banners leave, down to InputFieldBackground's own floor. While it
                          folds, the floor drops so the card can reach 0. */}
                      <MotionStack
                        {...composerFold.cardMotionProps}
                        flexShrink={1}
                        minHeight={isMobile || isComposerFolding ? 0 : '3rem'}
                        flexGrow={1}
                        display={composerFold.isCollapsed ? 'none' : 'flex'}
                        overflow={isComposerFolding ? 'hidden' : undefined}
                        data-testid="composer-card"
                      >
                        <InputField
                          ref={inputFieldRef}
                          fieldAutoSize={isMobile || chatInputAutoHeight}
                          fieldMinHeight="3rem"
                          conversation={conversation}
                          participants={participants}
                          messageTypeOptions={conversationTypeOptions}
                          onSubmit={submitConversationData}
                          files={files}
                          setFiles={setFiles}
                          selectedParticipantsState={selectedParticipantsState}
                          onCollapse={composerFold.fold}
                          isFolded={composerFold.isFolded}
                        />
                      </MotionStack>
                      {/* Hidden from hit-testing and the tab order once the card is fully open;
                          while folded it overflows so the pill shadows are not clipped. */}
                      <MotionStack
                        {...composerFold.stripMotionProps}
                        overflow={composerFold.isFolded ? 'visible' : 'hidden'}
                        visibility={isComposerFolding ? 'visible' : 'hidden'}
                      >
                        <ComposerStrip
                          active={composerFold.isFolded}
                          requesterFirstName={stripRequesterFirstName}
                          showKeyHints={!isMobile}
                          iconOnlyWhenNarrow={!isMobile}
                          onReply={() =>
                            composerFold.unfold(ConversationChannelTypeExtended.Chat)
                          }
                          onAddNote={() =>
                            composerFold.unfold(ConversationChannelTypeExtended.InternalNote)
                          }
                          onApplyMacro={() =>
                            inputFieldRef.current?.openMacros(() => composerFold.unfold())
                          }
                          onSnooze={() => inputFieldRef.current?.openSnooze()}
                          offer={composerFold.offer}
                        />
                      </MotionStack>
                    </>
                  )}
                </Stack>
              </ComposerBannersProvider>
            </Stack>
          </SmsSessionProvider>
        </ChatRegistryProvider>
      </NewMessagesAnchorProvider>
    </Wrapper>
  );
};

export const Chat = memo(ChatComponent);
